import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';

const { FiZap, FiCheck, FiClock, FiX, FiArrowRight, FiGift } = FiIcons;

const OfferSection = () => {
  const [timeLeft, setTimeLeft] = useState({
    days: 2,
    hours: 14,
    minutes: 32,
    seconds: 45
  });

  const [spotsLeft, setSpotsLeft] = useState(17);

  useEffect(() => {
    const timer = setInterval(() => {
      setTimeLeft(prev => {
        if (prev.seconds > 0) {
          return { ...prev, seconds: prev.seconds - 1 };
        } else if (prev.minutes > 0) {
          return { ...prev, minutes: prev.minutes - 1, seconds: 59 };
        } else if (prev.hours > 0) {
          return { ...prev, hours: prev.hours - 1, minutes: 59, seconds: 59 };
        } else if (prev.days > 0) {
          return { ...prev, days: prev.days - 1, hours: 23, minutes: 59, seconds: 59 };
        }
        return prev;
      });
    }, 1000);

    const spotsTimer = setInterval(() => {
      setSpotsLeft(prev => Math.max(1, prev - 1));
    }, 45000);

    return () => {
      clearInterval(timer);
      clearInterval(spotsTimer);
    };
  }, []);

  const handleCTAClick = () => {
    window.open('https://app.noem.ai/cb/growthshop', '_blank');
  };

  const offerItems = [
    {
      title: "Complete AI Chatbot Setup",
      value: "$999",
      included: true,
      description: "We build and customize everything for you"
    },
    {
      title: "Product Training & Integration",
      value: "$499",
      included: true,
      description: "Train AI on all your products and FAQs"
    },
    {
      title: "3 Months Enterprise Features",
      value: "$297",
      included: true,
      description: "Advanced analytics and priority support"
    },
    {
      title: "30 Days Post-Launch Support",
      value: "$499",
      included: true,
      description: "We optimize and tweak for maximum results"
    }
  ];

  const totalPrice = 999 + 499 + 297 + 499;

  return (
    <section id="offer" className="py-20 bg-gradient-to-br from-gray-900 via-black to-gray-900 text-white relative overflow-hidden">
      {/* Background Effects */}
      <div className="absolute inset-0">
        <div className="absolute inset-0 bg-gradient-to-r from-yellow-400/10 via-transparent to-orange-400/10 animate-pulse"></div>
      </div>

      <div className="container mx-auto px-6 relative">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="text-center mb-16"
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="inline-flex items-center gap-3 bg-gradient-to-r from-yellow-400 to-orange-400 text-black font-black px-8 py-4 rounded-full text-lg mb-8 shadow-2xl animate-pulse"
          >
            <SafeIcon icon={FiZap} className="text-2xl" />
            BLACK FRIDAY MEGA DEAL
            <SafeIcon icon={FiGift} className="text-2xl" />
          </motion.div>

          <h2 className="text-4xl md:text-6xl font-black mb-6 leading-tight">
            Get Everything For{' '}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-yellow-400 to-orange-400">
              $250
            </span>{' '}
            Instead of{' '}
            <span className="text-gray-500 line-through">${totalPrice}</span>
          </h2>

          <p className="text-xl text-gray-300 max-w-4xl mx-auto leading-relaxed">
            One-time payment. No monthly fees. Setup in 7 days. Results guaranteed or your money back.
          </p>
        </motion.div>

        {/* Urgency Section */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, delay: 0.2 }}
          className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto mb-12"
        >
          {/* Countdown Timer */}
          <div className="bg-gradient-to-r from-red-500/20 to-pink-500/20 backdrop-blur-xl rounded-3xl p-8 border border-red-400/30">
            <div className="flex items-center gap-3 mb-6">
              <SafeIcon icon={FiClock} className="text-red-400 text-2xl" />
              <h3 className="text-2xl font-bold text-red-400">Offer Expires In:</h3>
            </div>
            <div className="grid grid-cols-4 gap-4">
              {Object.entries(timeLeft).map(([unit, value]) => (
                <div key={unit} className="text-center">
                  <div className="bg-red-500/20 rounded-xl p-4 border border-red-400/30">
                    <div className="text-3xl font-black text-red-400">
                      {value.toString().padStart(2, '0')}
                    </div>
                    <div className="text-xs text-gray-400 capitalize mt-1">{unit}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Spots Left */}
          <div className="bg-gradient-to-r from-orange-500/20 to-yellow-500/20 backdrop-blur-xl rounded-3xl p-8 border border-orange-400/30">
            <div className="flex items-center gap-3 mb-6">
              <SafeIcon icon={FiX} className="text-orange-400 text-2xl" />
              <h3 className="text-2xl font-bold text-orange-400">Limited Spots Available:</h3>
            </div>
            <div className="text-center">
              <div className="text-6xl font-black text-orange-400 mb-2">{spotsLeft}</div>
              <div className="text-gray-400 mb-4">spots remaining</div>
              <div className="w-full bg-gray-700 rounded-full h-4">
                <div 
                  className="bg-gradient-to-r from-orange-400 to-yellow-400 h-4 rounded-full transition-all duration-500"
                  style={{ width: `${(spotsLeft / 50) * 100}%` }}
                ></div>
              </div>
              <div className="text-xs text-gray-500 mt-2">50 total spots for this offer</div>
            </div>
          </div>
        </motion.div>

        {/* What's Included */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, delay: 0.4 }}
          className="bg-white/10 backdrop-blur-xl rounded-3xl p-8 md:p-12 border border-white/20 max-w-5xl mx-auto mb-12"
        >
          <h3 className="text-3xl font-black text-center mb-8">Everything You Get Today:</h3>
          
          <div className="space-y-4 mb-8">
            {offerItems.map((item, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: 0.6 + index * 0.1 }}
                className="bg-white/5 rounded-2xl p-6 border border-white/10 hover:bg-white/10 transition-colors"
              >
                <div className="flex items-start gap-4">
                  <div className="w-8 h-8 bg-emerald-500 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                    <SafeIcon icon={FiCheck} className="text-white text-sm" />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center justify-between mb-2">
                      <h4 className="text-xl font-bold text-white">{item.title}</h4>
                      <span className="text-emerald-400 font-bold">Value: {item.value}</span>
                    </div>
                    <p className="text-gray-300">{item.description}</p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>

          {/* Pricing Comparison */}
          <div className="bg-gradient-to-r from-gray-800 to-gray-900 rounded-2xl p-6 text-center">
            <div className="grid md:grid-cols-3 gap-6 items-center">
              <div>
                <div className="text-gray-400 mb-2">Regular Price:</div>
                <div className="text-3xl font-bold line-through text-gray-500">${totalPrice}</div>
              </div>
              <div>
                <div className="text-yellow-400 font-bold mb-2">Black Friday Price:</div>
                <div className="text-5xl font-black text-yellow-400">$250</div>
                <div className="text-gray-400 text-sm">One-time payment</div>
              </div>
              <div>
                <div className="text-emerald-400 font-bold mb-2">You Save:</div>
                <div className="text-3xl font-black text-emerald-400">${totalPrice - 250}</div>
                <div className="text-gray-400 text-sm">86% discount</div>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Main CTA */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, delay: 0.6 }}
          className="text-center"
        >
          <motion.button
            onClick={handleCTAClick}
            whileHover={{ scale: 1.05, boxShadow: "0 20px 40px rgba(0,0,0,0.3)" }}
            whileTap={{ scale: 0.95 }}
            className="group relative w-full max-w-2xl bg-gradient-to-r from-yellow-400 to-orange-400 hover:from-yellow-500 hover:to-orange-500 text-black font-black py-6 px-12 rounded-2xl text-2xl shadow-2xl transition-all duration-300 overflow-hidden cursor-pointer"
          >
            <span className="relative z-10 flex items-center justify-center gap-3">
              <span>CLAIM MY $250 DEAL NOW</span>
              <SafeIcon icon={FiArrowRight} className="group-hover:translate-x-2 transition-transform text-2xl" />
            </span>
            <div className="absolute inset-0 bg-gradient-to-r from-white/30 to-transparent opacity-0 group-hover:opacity-100 transition-opacity"></div>
          </motion.button>

          <div className="flex flex-col sm:flex-row items-center justify-center gap-6 mt-6 text-gray-400">
            <div className="flex items-center gap-2">
              <SafeIcon icon={FiCheck} className="text-emerald-400" />
              <span>30-day money-back guarantee</span>
            </div>
            <div className="flex items-center gap-2">
              <SafeIcon icon={FiCheck} className="text-emerald-400" />
              <span>Setup in 7 days or less</span>
            </div>
            <div className="flex items-center gap-2">
              <SafeIcon icon={FiCheck} className="text-emerald-400" />
              <span>No hidden fees</span>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default OfferSection;